import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Scissors, ShieldAlert, SlidersHorizontal } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  type MyTaskSort,
  type ScheduleItem,
  TEAM_SCHEDULE_TASK_LIMIT,
  TEAM_TASK_LIMIT,
  TaskPriority,
  TeamTaskSort,
  WorkflowCategory,
} from '@apm/contracts';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorState, Skeleton } from '../../components/ui/states';
import { GlassSegmented } from '../../components/ui/glass-segmented';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { dashboardApi, dashboardKeys } from '../../features/dashboard/api';
import { myTasksApi, myTasksKeys } from '../../features/my-tasks/api';
import { CalendarView } from '../../features/my-tasks/components/calendar-view';
import { MonthCalendarView } from '../../features/my-tasks/components/month-calendar-view';
import { MyBugsSection } from '../../features/my-tasks/components/my-bugs-section';
import { MyInvitations } from '../../features/projects/components/my-invitations';
import { ScheduleTaskPopup } from '../../features/my-tasks/components/schedule-task-popup';
import { TaskList } from '../../features/my-tasks/components/task-list';
import { TodosTab } from '../../features/todos/components/todos-tab';
import {
  type TeamCalendarPick,
  TeamMonthCalendar,
  TeamWeekCalendar,
} from '../../features/my-tasks/components/team-calendar-view';
import { todayIso } from '../../lib/overdue';
import {
  SCHEDULE_UNITS,
  periodRange,
  shiftPeriod,
  type ScheduleUnit,
} from '../../features/my-tasks/week';
import { displayDateRange } from '../../lib/date';
import { errorMessage } from '../../lib/error-message';
import { cn } from '../../lib/utils';

/**
 * Toàn bộ bộ lọc và tuần đang xem nằm ở **search param** (frontend-guide §2).
 *
 * "Gửi cho tôi link danh sách việc quá hạn của bạn" chỉ hoạt động nếu trạng
 * thái đó nằm trong URL. Chôn vào `useState` thì ai mở link cũng rơi về mặc
 * định — và người gửi sẽ không biết.
 */
export const MyTasksSearch = z.object({
  /**
   * Việc của tôi hay việc đội của tôi — APM-MYT-c. Enum chuỗi, không dùng số:
   * router đọc giá trị bằng `JSON.parse`, và `?scope=1` sẽ tới đây dưới dạng số.
   */
  /** `todo` — tab To-do cá nhân (APM-TODO-c). Không có = tab Việc của tôi. */
  scope: z.enum(['me', 'todo', 'team']).optional(),
  /**
   * Hai bộ lọc chỉ của tab đội. uuid là chuỗi mà `JSON.parse` không đọc nổi nên
   * không dính bẫy APM-903b. `.catch(undefined)`: một link hỏng rơi về "không
   * lọc" thay vì làm trắng cả trang.
   */
  member: z.uuid().optional().catch(undefined),
  project: z.uuid().optional().catch(undefined),
  view: z.enum(['list', 'calendar']).optional(),
  status: WorkflowCategory.optional(),
  /**
   * "Chưa xong" — mọi nhóm trạng thái trừ `done` (APM-MYT-e). Tham số riêng chứ
   * không phải một giá trị của `status`: `status` là đúng một nhóm và đi thẳng
   * vào API. `z.boolean()` vì router `JSON.parse` giá trị, `?open=true` tới đây
   * là boolean (bẫy APM-903b). Loại trừ với `status` — `commonFilters` ưu tiên
   * `open` khi link mang cả hai, vì API trả 400 cho cặp ấy.
   */
  open: z.boolean().optional(),
  priority: TaskPriority.optional(),
  overdue: z.boolean().optional(),
  /** `TeamTaskSort` là `MyTaskSort` + `assignee`; tab của tôi tự bỏ `assignee`. */
  sort: TeamTaskSort.optional(),
  /** Chỉ dùng cho tab Lịch: xem theo tuần hay theo tháng. */
  unit: z.enum(SCHEDULE_UNITS).optional(),
  /**
   * Một ngày bất kỳ **trong** kỳ đang xem — không nhất thiết là ngày đầu kỳ.
   * `periodRange` tự suy ra khoảng hiển thị từ đây theo `unit`. Đổi tên từ
   * `week` vì giờ nó neo cả tuần lẫn tháng, không chỉ tuần.
   */
  anchor: z.string().optional(),
});

type Search = z.infer<typeof MyTasksSearch>;
type SearchPatch = Partial<Search>;
type Scope = 'me' | 'todo' | 'team';

export const Route = createFileRoute('/_app/my-tasks')({
  validateSearch: MyTasksSearch,
  component: MyTasksPage,
});

function MyTasksPage(): ReactNode {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Lịch là cách nhìn mặc định — người dùng chốt 2026-10-15. `?view=list` mở danh sách.
  const view = search.view ?? 'calendar';
  const unit: ScheduleUnit = search.unit ?? 'week';
  const today = todayIso();

  /*
    Tab đội chỉ có khi có cấp dưới trực tiếp — docs/plan-my-team-tasks.md §2.1.
    Cùng khoá với dashboard/topbar nên không thêm request nào.

    `?scope=team` trên link của người **không** có cấp dưới thì rơi về tab của
    tôi, không hiện một tab mà server sẽ trả 403.
  */
  const abilities = useQuery({
    queryKey: dashboardKeys.abilities,
    queryFn: dashboardApi.abilities,
    staleTime: 5 * 60 * 1000,
  });
  const leads = abilities.data?.hasDirectReports ?? false;
  const scope: Scope =
    search.scope === 'todo' ? 'todo' : leads && search.scope === 'team' ? 'team' : 'me';
  // Link `?scope=team` mở khi quyền chưa về: chờ, đừng vẽ tạm tab của tôi rồi
  // nhảy — lượt vẽ tạm ấy còn tốn một lời gọi `/me/tasks` không ai xem.
  const scopePending = search.scope === 'team' && abilities.isPending;

  const setSearch = (patch: SearchPatch): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  /*
    Đổi tab thì bỏ hai bộ lọc của tab đội, và bỏ `sort=assignee` — ở tab của
    tôi mọi dòng cùng một người nên cách xếp đó vô nghĩa. Các bộ lọc chung
    (trạng thái, ưu tiên, quá hạn) giữ nguyên: người ta so "việc quá hạn của tôi"
    với "việc quá hạn của đội" bằng đúng một cú bấm.
  */
  const switchScope = (next: Scope): void => {
    setSearch({
      scope: next === 'me' ? undefined : next,
      member: undefined,
      project: undefined,
      ...(search.sort === 'assignee' ? { sort: undefined } : {}),
    });
  };

  return (
    <div className="space-y-4">
      {/* Phones: the subtitle restates the page title, and on 390px it took two
          lines above the filters — hidden there so title and view switch share
          one line. */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t('nav.myTasks')}</h1>
          <p className="hidden text-sm text-muted-foreground sm:block">
            {scope === 'team'
              ? t('myTasks.team.subtitle')
              : scope === 'todo'
                ? t('todos.subtitle')
                : t('myTasks.subtitle')}
          </p>
        </div>

        {/* Danh sách / Lịch là cách nhìn **task**; tab To-do có bố cục riêng. */}
        {scope !== 'todo' && (
          // Danh sách / Lịch là cách nhìn, không phải tab: cụm nút kính
          // (`GlassSegmented`), không phải `Tabs`.
          <GlassSegmented
            value={view}
            options={(['list', 'calendar'] as const).map((option) => ({
              value: option,
              label: t(`myTasks.view.${option}`),
            }))}
            // Mặc định (lịch) không ghi lên URL — link gọn, và đổi mặc định về sau
            // không làm link cũ nói khác đi.
            onChange={(option) => setSearch({ view: option === 'calendar' ? undefined : option })}
            ariaLabel={t('myTasks.viewSwitch')}
          />
        )}
      </header>

      {/*
        Lời mời vào dự án — APM-INVOPEN. Trang chủ đá nhân viên sang đây, nên khối lời mời
        chỉ đứng ở trang chủ là nhân viên không bao giờ thấy nó (prod VOTE, 22/10). Tự ẩn
        khi rỗng.
      */}
      <MyInvitations />

      {/*
        Thanh tab luôn hiện từ APM-TODO-c: tab To-do là của **mọi người**. Tab
        đội vẫn chỉ có khi có cấp dưới trực tiếp. Thứ tự do người dùng chốt
        (2026-10-15): To-do · Việc của tôi · Việc đội của tôi. Tab mở mặc định vẫn
        là Việc của tôi.
      */}
      <Tabs value={scope} onValueChange={(value) => switchScope(value as Scope)}>
        <TabsList variant="glass" aria-label={t('myTasks.team.tabsLabel')}>
          <TabsTrigger value="todo">{t('myTasks.team.tab.todo')}</TabsTrigger>
          <TabsTrigger value="me">{t('myTasks.team.tab.me')}</TabsTrigger>
          {leads && <TabsTrigger value="team">{t('myTasks.team.tab.team')}</TabsTrigger>}
        </TabsList>
      </Tabs>

      {scope === 'todo' ? (
        <TodosTab />
      ) : scopePending ? (
        <Skeleton className="h-64 w-full" />
      ) : scope === 'team' ? (
        view === 'list' ? (
          <TeamListPane search={search} today={today} onChange={setSearch} />
        ) : (
          <TeamSchedulePane
            unit={unit}
            anchor={search.anchor ?? today}
            today={today}
            onChange={setSearch}
          />
        )
      ) : (
        <>
          {/* Lỗi là đầu việc như Jira (APM-TEST2-e): đứng trên việc, ở cả hai
              cách nhìn — lịch không vẽ được lỗi, nhưng không được giấu chúng. */}
          <MyBugsSection today={today} />
          {view === 'list' ? (
            <ListPane search={search} today={today} onChange={setSearch} />
          ) : (
            <SchedulePane
              unit={unit}
              anchor={search.anchor ?? today}
              today={today}
              onChange={setSearch}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/** Bộ lọc chung của hai tab: trạng thái (hoặc "chưa xong"), ưu tiên, quá hạn. */
function commonFilters(search: Search) {
  return {
    ...(search.open ? { open: true } : search.status ? { status: search.status } : {}),
    ...(search.priority ? { priority: search.priority } : {}),
    ...(search.overdue ? { overdue: true } : {}),
  };
}

const MY_SORTS = ['focus', 'dueDate', 'priority', 'project', 'updatedAt'] as const;
const TEAM_SORTS = [...MY_SORTS, 'assignee'] as const;

function ListPane({
  search,
  today,
  onChange,
}: {
  search: Search;
  today: string;
  onChange: (patch: SearchPatch) => void;
}): ReactNode {
  const { t } = useTranslation();

  /* Mặc định `focus`: việc đang làm lên trước, rồi việc sắp làm, việc đã xong
     xuống cuối — APM-ORG-h2. Xem `MyTaskSort` ở `@apm/contracts`. `assignee`
     chỉ có nghĩa ở tab đội; link mang nó sang đây thì rơi về mặc định. */
  const sort: MyTaskSort =
    search.sort === undefined || search.sort === 'assignee' ? 'focus' : search.sort;
  const filters = { ...commonFilters(search), sort };

  const tasks = useQuery({
    queryKey: myTasksKeys.list(filters),
    queryFn: () => myTasksApi.list(filters),
  });

  return (
    <>
      <FilterBar search={search} sort={sort} sorts={MY_SORTS} onChange={onChange} />

      {tasks.isPending && <Skeleton className="h-64 w-full" />}
      {tasks.isError && (
        <ErrorState message={errorMessage(tasks.error, t)} onRetry={() => void tasks.refetch()} />
      )}
      {tasks.isSuccess &&
        (tasks.data.length === 0 ? (
          <EmptyState title={t('myTasks.emptyTitle')} description={t('myTasks.emptyHelp')} />
        ) : (
          <TaskList tasks={tasks.data} today={today} />
        ))}
    </>
  );
}

/**
 * Danh sách việc của cấp dưới trực tiếp — APM-MYT-c, docs/plan-my-team-tasks.md §2.5.
 *
 * Hai `select` Người / Dự án lấy lựa chọn từ chính phản hồi (`members`,
 * `projects`), không từ một endpoint danh bạ: đội do server tự tính, và FE
 * không có cách nào khác để biết ai trong đó. `projects` không co theo bộ lọc
 * (xem `TeamTaskList`), nên đổi người không làm mất lựa chọn dự án.
 *
 * `placeholderData: keepPreviousData`: mỗi lần đổi bộ lọc là một query key mới;
 * không giữ dữ liệu cũ thì hai `select` biến mất trong lúc tải — đúng lúc người
 * dùng vừa bấm vào một trong hai.
 *
 * ⚠️ Không nhận `asOf` — danh sách việc hiện tại, không phải chuỗi đo theo thời
 * gian (luật #7 áp cho view vẽ đại lượng biến thiên), như danh sách của tôi.
 */
function TeamListPane({
  search,
  today,
  onChange,
}: {
  search: Search;
  today: string;
  onChange: (patch: SearchPatch) => void;
}): ReactNode {
  const { t } = useTranslation();

  const sort = search.sort ?? 'focus';
  const filters = {
    ...commonFilters(search),
    ...(search.member ? { userId: search.member } : {}),
    ...(search.project ? { projectId: search.project } : {}),
    sort,
  };

  const tasks = useQuery({
    queryKey: myTasksKeys.teamList(filters),
    queryFn: () => myTasksApi.teamList(filters),
    placeholderData: keepPreviousData,
  });

  const selectClass =
    'rounded-control border border-border-strong bg-background px-2 py-1 text-sm text-foreground';

  return (
    <>
      <FilterBar search={search} sort={sort} sorts={TEAM_SORTS} onChange={onChange}>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          {t('myTasks.team.filter.member')}
          <select
            value={search.member ?? ''}
            onChange={(event) => onChange({ member: event.target.value || undefined })}
            disabled={!tasks.data}
            className={selectClass}
          >
            <option value="">{t('myTasks.team.filter.all')}</option>
            {(tasks.data?.members ?? []).map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          {t('myTasks.team.filter.project')}
          <select
            value={search.project ?? ''}
            onChange={(event) => onChange({ project: event.target.value || undefined })}
            disabled={!tasks.data}
            className={selectClass}
          >
            <option value="">{t('myTasks.team.filter.all')}</option>
            {(tasks.data?.projects ?? []).map((project) => (
              <option key={project.projectId} value={project.projectId}>
                {project.key} · {project.name}
              </option>
            ))}
          </select>
        </label>
      </FilterBar>

      {/* Mỗi lần mở danh sách này đều ghi vào nhật ký truy cập của từng người có
          mặt trong đó (luật #5). Nói ra, như bảng hiệu suất đội. */}
      <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <ShieldAlert aria-hidden className="size-3.5 shrink-0" />
        {t('myTasks.team.audited')}
      </p>

      {tasks.isPending && <Skeleton className="h-64 w-full" />}
      {tasks.isError && (
        <ErrorState message={errorMessage(tasks.error, t)} onRetry={() => void tasks.refetch()} />
      )}
      {tasks.isSuccess && (
        <>
          {tasks.data.truncated && (
            // Cắt mà không nói thì "không thấy việc X" đọc thành "X không có việc".
            <p className="inline-flex items-center gap-1.5 rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/10 px-2 py-1 text-xs text-rag-amber">
              <Scissors aria-hidden className="size-3.5 shrink-0" />
              {t('myTasks.team.truncated', { count: TEAM_TASK_LIMIT })}
            </p>
          )}
          {tasks.data.items.length === 0 ? (
            <EmptyState
              title={t('myTasks.team.emptyTitle')}
              description={t('myTasks.team.emptyHelp')}
            />
          ) : (
            <TaskList tasks={tasks.data.items} today={today} variant="team" />
          )}
        </>
      )}
    </>
  );
}

/**
 * Thanh lọc dùng chung cho hai tab. `children` là bộ lọc riêng của tab (Người /
 * Dự án ở tab đội), đứng ngay trước ô sắp xếp để cả hàng đọc như một cụm.
 */
function FilterBar({
  search,
  sort,
  sorts,
  onChange,
  children,
}: {
  search: Search;
  sort: TeamTaskSort;
  sorts: readonly TeamTaskSort[];
  onChange: (patch: SearchPatch) => void;
  children?: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  /** Phones only: the two groups fold behind one "Bộ lọc" button. */
  const [expanded, setExpanded] = useState(false);
  const activeCount =
    (search.open || search.status ? 1 : 0) +
    (search.priority ? 1 : 0) +
    (search.member ? 1 : 0) +
    (search.project ? 1 : 0);

  /*
    On a phone the two bordered groups wrapped to two rows each (≈180px before
    the first task). Below `sm` they fold behind a "Bộ lọc · n" button, so the
    bar is one row — Bộ lọc · Quá hạn · Sắp xếp — and opening it drops the groups
    underneath (`max-sm:order-last`). From `sm` the button is gone and the
    wrapper is `display: contents`, i.e. the desktop row is unchanged.
  */
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant={activeCount > 0 ? 'default' : 'outline'}
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
        className="sm:hidden"
      >
        <SlidersHorizontal aria-hidden className="size-3.5" />
        {t('myTasks.filter.toggle')}
        {activeCount > 0 && <span className="tabular-nums">· {activeCount}</span>}
      </Button>

      <div className={cn('contents', !expanded && 'max-sm:hidden')}>
        {/* "Chưa xong" đứng trong nhóm Trạng thái vì nó cũng là một câu trả lời cho
          "trạng thái nào" — chọn nó thì bỏ `status` và ngược lại (APM-MYT-e). */}
        <FilterGroup
          tone="tag-1"
          label={t('myTasks.filter.status')}
          value={search.open ? 'open' : search.status}
          options={[
            { value: 'open' as const, label: t('myTasks.filter.open') },
            ...(['todo', 'in_progress', 'done'] as const).map((value) => ({
              value,
              label: t(`workflow.${value}`),
            })),
          ]}
          onSelect={(value) =>
            onChange(
              value === 'open'
                ? { open: true, status: undefined }
                : { open: undefined, status: value },
            )
          }
        />
        <FilterGroup
          tone="tag-2"
          label={t('myTasks.filter.priority')}
          value={search.priority}
          options={(['urgent', 'high', 'medium', 'low'] as const).map((value) => ({
            value,
            label: t(`priority.${value}`),
          }))}
          onSelect={(value) => onChange({ priority: value })}
        />
      </div>

      <Button
        type="button"
        variant={search.overdue ? 'default' : 'outline'}
        size="sm"
        onClick={() => onChange({ overdue: search.overdue ? undefined : true })}
      >
        {t('myTasks.filter.overdue')}
      </Button>

      {children && (
        <div
          className={cn(
            'contents [&>*]:max-sm:order-last [&>*]:max-sm:w-full [&>*]:max-sm:justify-between',
            !expanded && 'max-sm:hidden',
          )}
        >
          {children}
        </div>
      )}

      <label className="ml-auto flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
        <span className="max-sm:sr-only">{t('myTasks.filter.sort')}</span>
        <select
          value={sort}
          onChange={(event) => onChange({ sort: event.target.value as TeamTaskSort })}
          className="min-w-0 max-w-44 rounded-control border border-border-strong bg-background px-2 py-1 text-sm text-foreground sm:max-w-none"
        >
          {sorts.map((option) => (
            <option key={option} value={option}>
              {t(`myTasks.sort.${option}`)}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tuần hoặc tháng — spec chỉ nói "lịch", `unit` là chỗ chọn cách nhìn.
 *
 * Ba nút "Tuần trước/Tuần này/Tuần sau" cũ chỉ có nghĩa với tuần; đổi thành
 * hai mũi tên + một nút "Hôm nay" thì cùng ba nút đó dùng được cho cả tháng —
 * `shiftPeriod` biết nhích bảy ngày hay một tháng tuỳ `unit`, nút không cần
 * biết gì thêm.
 */
function SchedulePane({
  unit,
  anchor,
  today,
  onChange,
}: {
  unit: ScheduleUnit;
  anchor: string;
  today: string;
  onChange: (patch: SearchPatch) => void;
}): ReactNode {
  const { t } = useTranslation();
  const range = periodRange(anchor, unit);

  const schedule = useQuery({
    queryKey: myTasksKeys.schedule(range.from, range.to),
    queryFn: () => myTasksApi.schedule(range.from, range.to),
  });

  /**
   * Ngày nghỉ của chính người xem — APM-1319c.
   *
   * Lịch này trả lời "hôm đó tôi làm gì", nên một việc kéo dài hai tuần **không**
   * được hiện vào thứ Bảy của người chỉ làm T2–T6: nó đọc thành "phải đi làm
   * cuối tuần". Việc vẫn chạy qua ngày đó — chỗ nói điều ấy là Gantt.
   */
  const daysOff = useQuery({
    queryKey: myTasksKeys.daysOff(range.from, range.to),
    queryFn: () => myTasksApi.daysOff(range.from, range.to),
  });
  const offByDate = new Map((daysOff.data?.days ?? []).map((day) => [day.date, day.reason]));

  /**
   * Việc đang mở popup, giữ nguyên **item** chứ không chỉ id.
   *
   * `ScheduleItem` mang sẵn `projectName`/`projectKey` mà `TaskView` không có,
   * và popup cần cả hai. Giữ id rồi tìm lại trong `items` thì cú bấm sẽ hụt
   * đúng lúc danh sách vừa nạp lại (đổi tuần, hoặc sau khi cập nhật tiến độ).
   */
  const [opened, setOpened] = useState<ScheduleItem | null>(null);

  return (
    <>
      <PeriodNav unit={unit} anchor={anchor} today={today} onChange={onChange} />

      {schedule.isPending && <Skeleton className="h-64 w-full" />}
      {schedule.isError && (
        <ErrorState
          message={errorMessage(schedule.error, t)}
          onRetry={() => void schedule.refetch()}
        />
      )}
      {schedule.isSuccess &&
        (unit === 'week' ? (
          <CalendarView
            items={schedule.data.items}
            from={range.from}
            to={range.to}
            today={today}
            daysOff={offByDate}
            onOpen={setOpened}
          />
        ) : (
          <MonthCalendarView
            items={schedule.data.items}
            month={anchor}
            today={today}
            daysOff={offByDate}
          />
        ))}

      {opened === null ? null : <ScheduleTaskPopup item={opened} onClose={() => setOpened(null)} />}
    </>
  );
}

/**
 * Tuần/Tháng + hai mũi tên + "Hôm nay" + nhãn kỳ — dùng chung cho lịch của tôi
 * và lịch đội (APM-MYT-d), để hai lịch điều hướng y như nhau.
 */
function PeriodNav({
  unit,
  anchor,
  today,
  onChange,
}: {
  unit: ScheduleUnit;
  anchor: string;
  today: string;
  onChange: (patch: SearchPatch) => void;
}): ReactNode {
  const { t, i18n } = useTranslation();
  const range = periodRange(anchor, unit);

  // Dải 42 ngày của lưới tháng đọc như "31/08/2026 - 11/10/2026" không nói gì
  // về "tháng 9" — chỉ tuần mới hiện đúng khoảng đang tải.
  const locale = i18n.language === 'en' ? 'en-GB' : 'vi-VN';
  const periodLabel =
    unit === 'week'
      ? displayDateRange(range.from, range.to)
      : new Intl.DateTimeFormat(locale, {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(new Date(`${anchor}T00:00:00Z`));

  return (
    <div className="flex flex-wrap items-center gap-2">
      <nav
        className="flex gap-1 rounded-DEFAULT border border-border p-1"
        aria-label={t('myTasks.unitSwitch')}
      >
        {SCHEDULE_UNITS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange({ unit: option })}
            aria-current={unit === option}
            className={cn(
              'rounded-sm px-3 py-1 text-sm transition-colors',
              unit === option
                ? 'bg-muted font-medium text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(`myTasks.unit.${option}`)}
          </button>
        ))}
      </nav>

      <div className="flex items-center gap-1">
        <Button
          size="icon"
          variant="outline"
          aria-label={t('myTasks.prevPeriod')}
          onClick={() => onChange({ anchor: shiftPeriod(anchor, unit, -1) })}
        >
          <ChevronLeft aria-hidden />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onChange({ anchor: today })}>
          {t('myTasks.today')}
        </Button>
        <Button
          size="icon"
          variant="outline"
          aria-label={t('myTasks.nextPeriod')}
          onClick={() => onChange({ anchor: shiftPeriod(anchor, unit, 1) })}
        >
          <ChevronRight aria-hidden />
        </Button>
      </div>

      <span className="text-sm text-muted-foreground">{periodLabel}</span>
    </div>
  );
}

/**
 * Lịch đội — APM-MYT-d, docs/plan-my-team-tasks.md §2.5. Chỉ con số người ×
 * dự án × ngày; bấm một nhãn thì sang danh sách đội đã lọc theo người (+ dự
 * án), giữ nguyên `unit`/`anchor` để bấm "Lịch" là về đúng chỗ cũ.
 *
 * ⚠️ Không nhận `asOf` — lịch là kế hoạch hiện tại với trục `from`/`to` riêng
 * (ngoại lệ 2 của luật #7), như `SchedulePane`.
 */
function TeamSchedulePane({
  unit,
  anchor,
  today,
  onChange,
}: {
  unit: ScheduleUnit;
  anchor: string;
  today: string;
  onChange: (patch: SearchPatch) => void;
}): ReactNode {
  const { t } = useTranslation();
  const range = periodRange(anchor, unit);

  const schedule = useQuery({
    queryKey: myTasksKeys.teamSchedule(range.from, range.to),
    queryFn: () => myTasksApi.teamSchedule(range.from, range.to),
    placeholderData: keepPreviousData,
  });

  /* Lịch chỉ đếm việc chưa xong, nên danh sách mở ra cũng lọc "chưa xong" để
     khớp đúng con số vừa bấm — APM-MYT-e. Bỏ `status` vì hai cái loại trừ nhau. */
  const pick = ({ userId, projectId }: TeamCalendarPick): void =>
    onChange({ view: 'list', member: userId, project: projectId, open: true, status: undefined });

  return (
    <>
      <PeriodNav unit={unit} anchor={anchor} today={today} onChange={onChange} />

      <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <ShieldAlert aria-hidden className="size-3.5 shrink-0" />
        {t('myTasks.team.calendar.audited')}
      </p>

      {schedule.isPending && <Skeleton className="h-64 w-full" />}
      {schedule.isError && (
        <ErrorState
          message={errorMessage(schedule.error, t)}
          onRetry={() => void schedule.refetch()}
        />
      )}
      {schedule.isSuccess && (
        <>
          {schedule.data.truncated && (
            // Con số thiếu mà không nói thì "·2" trông như sự thật.
            <p className="inline-flex items-center gap-1.5 rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/10 px-2 py-1 text-xs text-rag-amber">
              <Scissors aria-hidden className="size-3.5 shrink-0" />
              {t('myTasks.team.calendar.truncated', { count: TEAM_SCHEDULE_TASK_LIMIT })}
            </p>
          )}
          {unit === 'week' ? (
            <TeamWeekCalendar view={schedule.data} today={today} onPick={pick} />
          ) : (
            <TeamMonthCalendar view={schedule.data} month={anchor} today={today} onPick={pick} />
          )}
          <p className="text-xs text-muted-foreground">{t('myTasks.team.calendar.hint')}</p>
        </>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hai lớp viết sẵn cho hai nhóm — không ghép `border-${tone}` lúc chạy: Tailwind
 * quét mã nguồn để biết sinh lớp nào, và một chuỗi ghép động thì không có mặt
 * trong mã nguồn nên không được sinh ra (lỗi chỉ thấy trên trình duyệt, xem
 * `task-type-tag.tsx`). Dùng token `--color-tag-*` — bộ màu tồn tại riêng cho
 * việc *phân biệt*, không mang nghĩa tốt/xấu như RAG — nên viền không bị đọc
 * nhầm thành cảnh báo.
 */
const GROUP_TONE = {
  'tag-1': 'border-tag-1/40',
  'tag-2': 'border-tag-2/40',
} as const;

/**
 * Nhóm nút lọc kiểu bật/tắt — bấm lại vào cái đang chọn thì bỏ lọc.
 *
 * Có viền bao quanh + nhãn hiện ra ngay trong khung, để "Trạng thái" và "Độ ưu
 * tiên" đọc rõ là hai nhóm tách biệt thay vì một dãy nút liền nhau không phân
 * ranh giới. Mỗi nhóm một màu viền (`tone`) để phân biệt còn nhanh hơn.
 */
function FilterGroup<T extends string>({
  tone,
  label,
  value,
  options,
  onSelect,
}: {
  tone: keyof typeof GROUP_TONE;
  label: string;
  value: T | undefined;
  options: { value: T; label: string }[];
  onSelect: (value: T | undefined) => void;
}): ReactNode {
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 rounded-DEFAULT border-2 px-2 py-1',
        'max-sm:order-last max-sm:w-full max-sm:flex-wrap',
        GROUP_TONE[tone],
      )}
      role="group"
      aria-label={label}
    >
      <span className="shrink-0 whitespace-nowrap px-1 text-xs font-medium text-muted-foreground max-sm:basis-full">
        {label}
      </span>
      {options.map((option) => (
        <Button
          key={option.value}
          type="button"
          size="sm"
          variant={value === option.value ? 'default' : 'outline'}
          aria-pressed={value === option.value}
          onClick={() => onSelect(value === option.value ? undefined : option.value)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}
